import { ICharacterQuestData, IVector3, QuestId, RewardType } from '../../types';
import { ConfigParser } from '../ConfigParser';

export enum DialogueReplyIcon {
	NONE,
	EXIT,
	QUEST,
	NEXT,
}

export interface IDialogueReplyAction {
	name: string;
	params: string[];
}

export interface IQuestsConfigDualogueReply {
	text: string;
	icon: DialogueReplyIcon;
	actions: IDialogueReplyAction[];
}

export class QuestsConfig {
	private tasksById = new Map<string, QuestsTaskConfig>();
	private questsById = new Map<string, QuestsQuestConfig>();
	private npcsById = new Map<string, QuestsNpcConfig>();
	private dialoguesById = new Map<string, QuestsDialogueConfig>();
	private vehiclesById = new Map<string, QuestsVehicleConfig>();

	private questTargetIds = new Set<string>();

	constructor(tables: Record<string, any>) {
		for (const rawTask of tables['tasks']) {
			const taskConfig = new QuestsTaskConfig(rawTask);
			if (this.tasksById.has(taskConfig.id)) {
				throw new Error(`Duplicate task: ${taskConfig.id}`);
			}

			this.tasksById.set(taskConfig.id, taskConfig);
		}

		for (const rawQuest of tables['quests']) {
			const questConfig = new QuestsQuestConfig(rawQuest);
			if (this.questsById.has(questConfig.id)) {
				throw new Error(`Duplicate quest: ${questConfig.id}`);
			}

			this.questsById.set(questConfig.id, questConfig);
		}

		for (const rawNpc of tables['npcs']) {
			const npcConfig = new QuestsNpcConfig(rawNpc);
			if (this.npcsById.has(npcConfig.id)) {
				throw new Error(`Duplicate NPC: ${npcConfig.id}`);
			}

			this.npcsById.set(npcConfig.id, npcConfig);
		}

		for (const rawDialogue of tables['dialogues']) {
			const dialogueConfig = new QuestsDialogueConfig(rawDialogue, this);
			if (this.dialoguesById.has(dialogueConfig.id)) {
				throw new Error(`Duplicate dialogue: ${dialogueConfig.id}`);
			}

			this.dialoguesById.set(dialogueConfig.id, dialogueConfig);
		}

		// Валидируем зависимости между таблицами
		for (const questConfig of this.questsById.values()) {
			if (!this.tasksById.has(questConfig.taskId)) {
				throw new Error(`Quest ${questConfig.id} references task ${questConfig.taskId}, but it does not exist`);
			}

			for (const questId of questConfig.dependsOn) {
				if (!this.questsById.has(questId)) {
					throw new Error(`Quest ${questConfig.id} depends on quest ${questId}, but it does not exist`);
				}
			}

			if (questConfig.nextQuestId && !this.questsById.has(questConfig.nextQuestId)) {
				throw new Error(
					`Quest ${questConfig.id} references next quest ${questConfig.nextQuestId}, but it does not exist`,
				);
			}
		}

		// Валидируем зависимости в condition и npcId
		for (const dialogueConfig of this.dialoguesById.values()) {
			for (const condition of dialogueConfig.conditions) {
				if (!this.questsById.has(condition.id)) {
					throw new Error(
						`Dialogue ${dialogueConfig.id} visibleWhen conditions contains non-existent quest ${condition.id} with state ${condition.state}`,
					);
				}
			}

			if (dialogueConfig.npcId && !this.npcsById.has(dialogueConfig.npcId)) {
				throw new Error(
					`Dialogue ${dialogueConfig.id} references NPC ${dialogueConfig.npcId}, but it does not exist`,
				);
			}
		}

		for (const questConfig of this.questsById.values()) {
			this.questTargetIds.add(questConfig.taskId);
		}

		const uniqueVehicles = new Set<string>();
		for (const rawVehicle of tables['vehicles']) {
			const vehicleConfig = new QuestsVehicleConfig(rawVehicle);

			if (uniqueVehicles.has(vehicleConfig.id)) {
				throw new Error(`Duplicated quest vehicle: ${vehicleConfig.id}`);
			}
			uniqueVehicles.add(vehicleConfig.id);

			for (const questId of vehicleConfig.quests) {
				if (!this.questsById.has(questId)) {
					throw new Error(`Quest ${questId} for vehicle ${vehicleConfig.id} does not exists`);
				}

				// Максимум одно ТС на квест
				this.vehiclesById.set(questId, vehicleConfig);
			}
		}
	}

	public getVehicleById(id: string): QuestsVehicleConfig | null {
		return this.vehiclesById.get(id) || null;
	}

	public getTaskById(id: string): QuestsTaskConfig | null {
		return this.tasksById.get(id) || null;
	}

	public getQuestById(id: string): QuestsQuestConfig | null {
		return this.questsById.get(id) || null;
	}

	public getTaskByQuestId(id: string): QuestsTaskConfig | null {
		const questConfig = this.getQuestById(id);
		if (!questConfig) {
			return null;
		}

		return this.getTaskById(questConfig.taskId);
	}

	public getQuestByTaskId(id: string): QuestsQuestConfig | null {
		for (const questConfig of this.questsById.values()) {
			if (questConfig.taskId === id) {
				return questConfig;
			}
		}

		return null;
	}

	public getAllQuestIds(): string[] {
		return Array.from(this.questsById.keys());
	}

	public getAvailableQuestsIds(questProgressById: Map<string, ICharacterQuestData>): string[] {
		const questConfigs = Array.from(this.questsById.entries());
		const unlockedQuestConfigs = questConfigs.filter(([, questConfig]) => {
			if (!questConfig.dependsOn || questConfig.dependsOn.length === 0) {
				return true;
			}

			const isUnlocked = questConfig.dependsOn.every((depId) => questProgressById.get(depId)?.isFinished);
			return isUnlocked;
		});

		return unlockedQuestConfigs.map(([questId]) => questId);
	}

	public getQuestTargetIds(): Set<string> {
		return this.questTargetIds;
	}

	public getClientConfig() {
		return {
			tasks: Array.from(this.tasksById.values()).map((e) => e.getClientConfig()),
			quests: Array.from(this.questsById.values()).map((e) => e.getClientConfig()),
			dialogues: Array.from(this.dialoguesById.values()).map((e) => e.getClientConfig()),
			npcs: Array.from(this.npcsById.values()).map((e) => e.getClientConfig()),
		};
	}
}

export class QuestsQuestConfig {
	public readonly id: QuestId;
	public readonly dependsOn: string[];
	public readonly taskId: string;
	public readonly reward: Array<[RewardType, string]> = [];
	public readonly nextQuestId: string | null;

	public readonly spawnsVehicle: boolean;
	public readonly destroysVehicle: boolean;

	constructor(rawConfig: Record<string, any>) {
		this.id = ConfigParser.getEnum(rawConfig, 'id', QuestId);
		this.dependsOn = ConfigParser.getStringArray(rawConfig, 'depends_on');
		this.taskId = ConfigParser.getString(rawConfig, 'task_id');

		const rewardStrs = ConfigParser.getStringArray(rawConfig, 'reward');
		this.reward = rewardStrs.map((e) => e.trim().split(':') as [RewardType, string]);

		const validRewardTypes = Object.values(RewardType);
		for (const [type] of this.reward) {
			if (!validRewardTypes.includes(type)) {
				throw new Error(`Invalid reward type "${type}" for quest "${this.id}"`);
			}
		}

		this.spawnsVehicle = ConfigParser.getBool(rawConfig, 'spawn_vehicle_on_start');
		this.destroysVehicle = ConfigParser.getBool(rawConfig, 'destroy_vehicle_on_finish');

		this.nextQuestId = ConfigParser.getString(rawConfig, 'next_quest_id', null);
	}

	public getClientConfig() {
		return {
			id: this.id,
			dependsOn: this.dependsOn,
			taskId: this.taskId,
			reward: this.reward,
		};
	}
}

export class QuestsTaskConfig {
	public readonly id: string;
	public readonly count: number;
	public readonly text: string;
	public readonly word: string;

	constructor(rawConfig: Record<string, any>) {
		this.id = ConfigParser.getString(rawConfig, 'id');
		this.count = ConfigParser.getInt(rawConfig, 'count');
		this.text = ConfigParser.getString(rawConfig, 'text');
		this.word = ConfigParser.getString(rawConfig, 'word');
	}

	public getClientConfig() {
		return {
			id: this.id,
			count: this.count,
			text: this.text,
			word: this.word,
		};
	}
}

export interface IVisibleNpcCondition {
	state: 'quest_before' | 'quest_active' | 'quest_ready' | 'quest_after';
	id: string;
}

export class QuestsNpcConfig {
	public readonly id: string;
	public readonly name: string;
	public readonly position: IVector3;
	public readonly heading: number;
	public readonly model: string;

	public readonly blipColor: number;
	public readonly blipSprite: number;
	public readonly secondaryText: string | null;

	constructor(rawConfig: Record<string, any>) {
		this.id = ConfigParser.getString(rawConfig, 'id');
		this.name = ConfigParser.getString(rawConfig, 'name');
		this.position = ConfigParser.getVector3(rawConfig, 'position');
		this.heading = ConfigParser.getFloat(rawConfig, 'heading');
		this.model = ConfigParser.getString(rawConfig, 'model');

		this.blipColor = ConfigParser.getInt(rawConfig, 'blip_color');
		this.blipSprite = ConfigParser.getInt(rawConfig, 'blip_sprite');

		this.secondaryText = ConfigParser.getString(rawConfig, 'secondary_text', null);
	}

	public getClientConfig() {
		return {
			id: this.id,
			name: this.name,
			position: this.position,
			heading: this.heading,
			model: this.model,

			blipColor: this.blipColor,
			blipSprite: this.blipSprite,

			secondaryText: this.secondaryText,
		};
	}
}

export interface IReplyCondition {
	state: 'quest_before' | 'quest_active' | 'quest_ready' | 'quest_after';
	id: string;
}

export class QuestsDialogueConfig {
	public readonly id: string;
	public readonly title: string;
	public readonly text: string;
	public readonly reward: Array<[RewardType, string]> = [];
	public readonly replies: IQuestsConfigDualogueReply[] = [];
	public readonly conditions: IReplyCondition[] = [];
	public readonly npcId: string;
	public readonly isInitial: boolean;

	constructor(rawConfig: Record<string, any>, questsConfig: QuestsConfig) {
		this.id = ConfigParser.getString(rawConfig, 'id');
		this.text = ConfigParser.getString(rawConfig, 'text');
		this.title = ConfigParser.getString(rawConfig, 'title');

		const rewardsQuestId = ConfigParser.getString(rawConfig, 'quest_reward', null);
		const reward = rewardsQuestId ? questsConfig.getQuestById(rewardsQuestId)?.reward : null;
		if (reward) {
			this.reward = reward;
		}

		for (let n = 1; n <= 4; n++) {
			if (!rawConfig[`reply_text_${n}`]) {
				continue;
			}

			const text = ConfigParser.getString(rawConfig, `reply_text_${n}`);
			const icon = ConfigParser.getEnum(rawConfig, `reply_icon_${n}`, DialogueReplyIcon, null);
			const actionStrings = ConfigParser.getStringArray(rawConfig, `reply_actions_${n}`);
			const actions = actionStrings.map((segment) => {
				const idx = segment.indexOf(':');
				const name = idx === -1 ? segment : segment.slice(0, idx).trim();
				const paramsStr = idx === -1 ? '' : segment.slice(idx + 1).trim();
				const params = paramsStr ? paramsStr.split(',').map((p) => p.trim()) : [];
				return { name, params };
			});

			const reply: IQuestsConfigDualogueReply = {
				text,
				icon: icon ?? this.getReplyIconFromAction(actions[0]),
				actions,
			};
			this.replies.push(reply);
		}

		if (this.replies.length === 0) {
			throw new Error(`Dialogue ${this.id} has no replies`);
		}

		const conditions = ConfigParser.getStringArray(rawConfig, 'conditions');
		for (const condition of conditions) {
			const [state, id] = condition.trim().split(':');
			this.conditions.push({
				state,
				id,
			} as IReplyCondition);
		}

		this.npcId = ConfigParser.getString(rawConfig, 'npc_id');
		this.isInitial = ConfigParser.getBool(rawConfig, 'is_initial');

		if (this.isInitial && !this.npcId) {
			throw new Error(`NPC id was not assigned for dialogue "${this.id}" with is_initial flag set`);
		}
	}

	private getReplyIconFromAction(action: IDialogueReplyAction | undefined): DialogueReplyIcon {
		if (!action) {
			return DialogueReplyIcon.NONE;
		}

		switch (action.name) {
			case 'exit':
				return DialogueReplyIcon.EXIT;
			case 'quest_start':
			case 'quest_finish':
				return DialogueReplyIcon.QUEST;
			case 'next':
				return DialogueReplyIcon.NEXT;
			default:
				return DialogueReplyIcon.NONE;
		}
	}

	public getClientConfig() {
		return {
			id: this.id,
			text: this.text,
			title: this.title,
			replies: this.replies,
			reward: this.reward,
			conditions: this.conditions,
			npcId: this.npcId,
			isInitial: this.isInitial,
		};
	}
}

export class QuestsVehicleConfig {
	public readonly id: string;
	public readonly position: IVector3;
	public readonly heading: number;
	public readonly model: string;

	public readonly quests: string[] = [];

	constructor(rawConfig: Record<string, any>) {
		this.id = ConfigParser.getString(rawConfig, 'id');
		this.position = ConfigParser.getVector3(rawConfig, 'position');
		this.heading = ConfigParser.getFloat(rawConfig, 'heading');
		this.model = ConfigParser.getString(rawConfig, 'model');

		this.quests = ConfigParser.getStringArray(rawConfig, 'quests');
	}
}
